// Post data selectors for cache-based access

/**
 * Get post data from cache with repost context
 * @param {Object} feedState - Redux state
 * @param {string} postId - Post ID
 * @param {string} repostUserId - Repost user ID (optional)
 * @returns {Object|null} Post data with repost context or null if not cached
 */
export const getPostDataFromCache = (feedState, postId, repostUserId = null) => {
  const cache = feedState.postCache.byId[postId];
  if (!cache) {
    return null; // Cache miss
  }

  // Check if cache is expired
  if (cache.expiry && cache.expiry <= Date.now()) {
    return null; // Cache expired
  }

  const baseData = cache.data;

  // If repost context is needed, merge it
  if (repostUserId && cache.reposts[repostUserId]) {
    return {
      ...baseData,
      repost: cache.reposts[repostUserId],
    };
  }

  return baseData;
};

/**
 * Check if post data is cached and not expired
 * @param {Object} state - Redux state
 * @param {string} postId - Post ID
 * @returns {boolean} True if cached and not expired
 */
export const isPostDataCached = (feedState, postId) => {
  const cache = feedState.postCache.byId[postId];
  if (!cache) {
    return false;
  }

  // Check if cache is expired
  if (cache.expiry && cache.expiry <= Date.now()) {
    return false;
  }

  return true;
};

/**
 * Get post data for a specific post in a feed
 * @param {Object} state - Redux state
 * @param {string} feedType - 'following' or 'trending'
 * @param {number} index - Post index in feed
 * @returns {Object|null} Post data with cache reference or null
 */
export const getPostWithCacheData = (feedState, feedType, index) => {
  const feed = feedState.feeds[feedType];
  if (!feed?.posts || !feed.posts[index]) {
    return null;
  }

  const post = feed.posts[index];
  const postId = post.postid || post.id || post._id;
  const repostUserId = post.repostuser;

  if (!postId) {
    return post; // Return original post if no ID
  }

  // Get data from cache (with expiry checks)
  const cachedData = getPostDataFromCache(feedState, postId, repostUserId);

  return {
    ...post,
    postData: cachedData, // Reference to cached data
    isCached: !!cachedData,
  };
};
